import * as React from 'react';
import {Button} from '../../button';
import {Dialog, DialogContent, DialogDescription, DialogTitle} from '../../dialog';
import {Drawer, DrawerContent, DrawerDescription, DrawerTitle} from '../../drawer';
import {AirbnbDatePickerWheelColumn} from './DatePickerWheelColumn';
import {type DatePickerColumn} from './useDatePickerWheel';

type AirbnbDatePickerContentProps = {
  baseId: string;
  open: boolean;
  isMobile: boolean;
  label: string;
  title: string;
  doneLabel: string;
  monthLabels: string[];
  days: number[];
  years: number[];
  monthIndex: number;
  dayIndex: number;
  yearIndex: number;
  monthScrollTop: number;
  dayScrollTop: number;
  yearScrollTop: number;
  monthListRef: React.RefObject<HTMLDivElement>;
  dayListRef: React.RefObject<HTMLDivElement>;
  yearListRef: React.RefObject<HTMLDivElement>;
  onOpenChange: (nextOpen: boolean) => void;
  onDone: () => void;
  onColumnScroll: (column: DatePickerColumn) => void;
  onColumnKeyDown: (
    column: DatePickerColumn,
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => void;
  onOptionSelect: (column: DatePickerColumn, targetIndex: number) => void;
};

function AirbnbDatePickerBody({
  baseId,
  label,
  doneLabel,
  monthLabels,
  days,
  years,
  monthIndex,
  dayIndex,
  yearIndex,
  monthScrollTop,
  dayScrollTop,
  yearScrollTop,
  monthListRef,
  dayListRef,
  yearListRef,
  onColumnScroll,
  onColumnKeyDown,
  onOptionSelect,
  onDone,
}: Omit<AirbnbDatePickerContentProps, 'open' | 'isMobile' | 'title' | 'onOpenChange'>) {
  return (
    <div className="px-6 pb-4 pt-1 bg-white">
      <div className="relative overflow-hidden rounded-[24px]">
        <div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-16 bg-gradient-to-b from-white via-white/80 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-16 bg-gradient-to-t from-white via-white/80 to-transparent" />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-1/2 z-0 h-8 -translate-y-1/2 rounded-[12px] bg-black/[0.04]"
        />
        <div className="relative grid grid-cols-[1.35fr_0.7fr_1fr] gap-1">
          <AirbnbDatePickerWheelColumn
            id={`${baseId}-month`}
            column="month"
            label={`${label} month`}
            items={monthLabels}
            activeIndex={monthIndex}
            scrollTop={monthScrollTop}
            listRef={monthListRef}
            onScroll={onColumnScroll}
            onKeyDown={onColumnKeyDown}
            onOptionSelect={onOptionSelect}
          />
          <AirbnbDatePickerWheelColumn
            id={`${baseId}-day`}
            column="day"
            label={`${label} day`}
            items={days.map(day => String(day))}
            activeIndex={dayIndex}
            scrollTop={dayScrollTop}
            listRef={dayListRef}
            onScroll={onColumnScroll}
            onKeyDown={onColumnKeyDown}
            onOptionSelect={onOptionSelect}
          />
          <AirbnbDatePickerWheelColumn
            id={`${baseId}-year`}
            column="year"
            label={`${label} year`}
            items={years.map(year => String(year))}
            activeIndex={yearIndex}
            scrollTop={yearScrollTop}
            listRef={yearListRef}
            onScroll={onColumnScroll}
            onKeyDown={onColumnKeyDown}
            onOptionSelect={onOptionSelect}
          />
        </div>
      </div>
      <Button type="button" onClick={onDone} className="mt-4 h-12 mb-8 w-full">
        {doneLabel}
      </Button>
    </div>
  );
}

function AirbnbDatePickerContent({
  baseId,
  open,
  isMobile,
  label,
  title,
  doneLabel,
  monthLabels,
  days,
  years,
  monthIndex,
  dayIndex,
  yearIndex,
  monthScrollTop,
  dayScrollTop,
  yearScrollTop,
  monthListRef,
  dayListRef,
  yearListRef,
  onOpenChange,
  onDone,
  onColumnScroll,
  onColumnKeyDown,
  onOptionSelect,
}: AirbnbDatePickerContentProps) {
  const body = (
    <AirbnbDatePickerBody
      baseId={baseId}
      label={label}
      doneLabel={doneLabel}
      monthLabels={monthLabels}
      days={days}
      years={years}
      monthIndex={monthIndex}
      dayIndex={dayIndex}
      yearIndex={yearIndex}
      monthScrollTop={monthScrollTop}
      dayScrollTop={dayScrollTop}
      yearScrollTop={yearScrollTop}
      monthListRef={monthListRef}
      dayListRef={dayListRef}
      yearListRef={yearListRef}
      onColumnScroll={onColumnScroll}
      onColumnKeyDown={onColumnKeyDown}
      onOptionSelect={onOptionSelect}
      onDone={onDone}
    />
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent
          onClose={() => onOpenChange(false)}
          className="rounded-none rounded-t-[32px] border-0 p-0"
        >
          <DrawerTitle className="sr-only">{title}</DrawerTitle>
          <DrawerDescription className="sr-only">{label}</DrawerDescription>
          {body}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[520px] rounded-[28px] border-0 p-0 shadow-xl"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">{label}</DialogDescription>
        {body}
      </DialogContent>
    </Dialog>
  );
}

export {AirbnbDatePickerContent};
